'use client';
import { PremiumBackground } from '../effects/PremiumBackground';

import React from 'react';
import { motion } from 'framer-motion';
import { 
ArrowDown, 
  FileText, 
  Brain, 
  RefreshCw, 
  Calculator, 
  Building2, 
  Scale, 
  UserCheck, 
  BarChart3, 
  ShieldCheck, 
  ArrowRight
} from 'lucide-react';

// Drop-in Link component for Vite React SPA
const Link = ({
  href,
  children,
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => (
  <a href={href} className={className} {...props}>
    {children}
  </a>
);

export interface AdaptiveRemediationProps {
  id?: string;
}

export function AdaptiveRemediation({ id = 'adaptive-remediation' }: AdaptiveRemediationProps = {}) {
  // Микроразметка Schema.org для нейросетей (SearchGPT, Perplexity, Яндекс Нейро, Google AI)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Адаптивное устранение дефектов и рисков клиники перед сделкой (Remediation)",
    "provider": {
      "@type": "Organization",
      "name": "Structure & Partners"
    },
    "description": "Методология адаптивного устранения уязвимостей медицинского бизнеса без остановки операционной деятельности и кадровых погромов: 1) Внедрение СОПов и первичного учета по Постановлению № 736; 2) Внешнее кураторство Interim CFO и налоговых адвокатов (176-ФЗ, НДС, NWC Peg); 3) Точечная кадровая ротация при рисках ст. 238 и 238.1 УК РФ.",
    "serviceType": "Healthcare Pre-M&A Remediation & Operational Advisory",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "5 модулей точечного усиления медицинского бизнеса",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "Финансы и кассовые разрывы",
          "description": "Ввод Временного финдиректора (Interim CFO), 3-связная модель (P&L, Cash Flow, Баланс), оцифровка себестоимости кресло-часа"
        },
        {
          "@type": "Offer",
          "name": "Налоговая реструктуризация",
          "description": "Консолидация по закону № 176-ФЗ (Амнистия) и подтверждение нулевой ставки налога на прибыль (ст. 284.1 НК РФ)"
        },
        {
          "@type": "Offer",
          "name": "Регуляторная и лицензионная броня",
          "description": "Узаконивание планировок рентген-кабинетов в БТИ, актуализация СЭЗ, переоформление договоров на опасные отходы (Классы Б и Г)"
        },
        {
          "@type": "Offer",
          "name": "Удержание врачей-звезд",
          "description": "Внедрение фантомных долей (Phantom Equity) и соглашений об удержании (Retention Plans)"
        },
        {
          "@type": "Offer",
          "name": "Коммерческий контур",
          "description": "Аудит воронки в МИС, проверка реальной стоимости привлечения пациента (CAC) и оптимизация рекламных бюджетов"
        }
      ]
    }
  };

  // 5 модулей спецназа
  const specializedModules = [
    {
      num: "01",
      icon: <Calculator size={18} className="text-[#B45309]" />,
      domain: "Финансы и кассовые разрывы",
      role: "Ввод Временного финдиректора (Interim CFO)",
      task: "Внедрение сквозной 3-связной финансовой модели (P&L, Cash Flow, Баланс), оцифровка себестоимости кресло-часа и ликвидация кассовых провалов."
    },
    {
      num: "02",
      icon: <Building2 size={18} className="text-[#B45309]" />,
      domain: "Налоговая реструктуризация",
      role: "Налоговые адвокаты бутика",
      task: "Безопасная консолидация дробленного бизнеса по закону № 176-ФЗ (Амнистия) и подтверждение права на нулевую ставку по налогу на прибыль (ст. 284.1 НК РФ)."
    },
    {
      num: "03",
      icon: <Scale size={18} className="text-[#B45309]" />,
      domain: "Регуляторная и лицензионная броня",
      role: "Медицинские юристы",
      task: "Узаконивание планировок рентген-кабинетов в БТИ, актуализация СЭЗ, переоформление договоров на опасные отходы (Классы Б и Г) по новым правилам 2026 года."
    },
    {
      num: "04",
      icon: <UserCheck size={18} className="text-[#B45309]" />,
      domain: "Удержание врачей-звезд",
      role: "HR-архитектура лояльности",
      task: "Внедрение фантомных долей (Phantom Equity) и соглашений об удержании (Retention Plans), защищающих клинику от увода пациентов ведущими докторами."
    },
    {
      num: "05",
      icon: <BarChart3 size={18} className="text-[#B45309]" />,
      domain: "Коммерческий контур",
      role: "Аналитики медицинского маркетинга",
      task: "Сквозной аудит воронки в МИС, проверка реальной стоимости привлечения пациента (CAC) и очистка базы от неэффективных рекламных расходов."
    }
  ];

  return (
    <section 
      id={id} 
      className="relative w-full py-28 md:py-36 bg-[#FAF8F5] text-[#141416] border-t border-[#EAE5DE] overflow-hidden"
      aria-label="Адаптивное устранение уязвимостей"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={4} />

      
      

      <div id="adaptive-remediation-anchor" className="absolute -top-16 left-0" />
      
      {/* Light subtle archival paper gradient */}
      <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[#F2EDE4]/60 to-transparent pointer-events-none" />

      {/* Скрытая разметка JSON-LD для ИИ-поисковиков */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-16 md:gap-20">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, H2 И ПОДЗАГОЛОВОК-МАНИФЕСТ
            ========================================================================= */}
        <div className="flex flex-col gap-6 max-w-4xl">
          <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2.5 font-bold tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
            ЭТАЖ 8 // ТОЧЕЧНОЕ УСТРАНЕНИЕ РИСКОВ // АДАПТИВНАЯ РЕМЕДИАЦИЯ
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold tracking-tight leading-[1.1] text-[#141416]">
            Шаг 3. Адаптивное устранение дефектов: <br />
            <span className="text-[#64748B] italic font-serif">закрываем уязвимости без паники и резких увольнений.</span>
          </h2>

          <p className="text-[16px] sm:text-[17px] text-[#334155] leading-[1.65] font-sans font-normal max-w-3xl border-l-[4px] border-[#8C6D37] pl-6 mt-4">
            Мы не приходим в клинику, чтобы устраивать кадровые погромы и разрушать то, что вы строили годами. <br />
            Наш подход — хирургическая адаптивность. Мы точно определяем, где достаточно внедрить регламент для медсестер, 
            где нужно поддержать главного бухгалтера внешним экспертом, и только в крайних случаях предлагаем замену людей.
          </p>
        </div>

        {/* =========================================================================
            2. ТРИ УРОВНЯ ИНСТИТУЦИОНАЛЬНОГО ВМЕШАТЕЛЬСТВА (HORIZONTAL CARDS)
            ========================================================================= */}
        <div className="flex flex-col gap-6">
          <span className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase">
            ТРИ УРОВНЯ ИНСТИТУЦИОНАЛЬНОГО ВМЕШАТЕЛЬСТВА // ADAPTIVE LEVELS:
          </span>

          <div className="flex flex-col gap-6">
            {/* УРОВЕНЬ 1 */}
            <div className="bg-white p-8 sm:p-10 rounded-[2px] border border-[#EAE5DE] shadow-xs flex flex-col lg:flex-row lg:items-start justify-between gap-8 hover:border-[#CBD5E1] transition-colors">
              <div className="flex flex-col gap-4 lg:max-w-[340px]">
                <div className="flex items-center gap-2.5">
                  <FileText size={16} className="text-[#64748B]" />
                  <span className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase">
                    УРОВЕНЬ 1. РЕГЛАМЕНТЫ
                  </span>
                </div>
                <h3 className="text-2xl font-serif font-semibold text-[#141416] leading-snug">
                  Настройка процессов и первичного учета <br />
                  <span className="text-[14px] font-sans font-normal text-[#64748B]">(Без кадровых изменений)</span>
                </h3>
                <span className="font-mono text-[10px] text-[#64748B] bg-[#F7F4EE] border border-[#EAE5DE] px-2.5 py-1 rounded-[2px] w-fit font-bold uppercase tracking-wider">
                  ПЕРСОНАЛ НЕ ТРОГАЕМ
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-[14px] font-sans font-normal text-[#334155] leading-relaxed flex-1 border-t lg:border-t-0 lg:border-l border-[#EAE5DE] pt-6 lg:pt-0 lg:pl-10">
                <div>
                  <strong className="text-[#141416] font-bold block mb-1.5">Когда применяется:</strong>
                  Коллектив лоялен и профессионален, но учет велся по инерции, а требования законов изменились.
                </div>
                <div>
                  <strong className="text-[#141416] font-bold block mb-1.5">Что делаем мы:</strong>
                  Мы не трогаем людей. Мы внедряем понятные пошаговые инструкции (СОПы): устраняем задержки ввода накладных, 
                  обучаем старших медсестер списывать расходники строго со счета 10, настраиваем в программе шаблоны договоров 
                  по Постановлению № 736 и согласий пациентов (ИДС). Команда работает спокойнее, риски исчезают.
                </div>
              </div>
            </div>

            {/* УРОВЕНЬ 2 (ФОКУСНЫЙ - ЗОЛОТО/БРОНЗА) */}
            <div className="bg-[#8C6D37]/10 p-8 sm:p-10 rounded-[2px] border border-[#8C6D37]/50 flex flex-col lg:flex-row lg:items-start justify-between gap-8 relative shadow-xs">
              <div className="absolute -top-3 left-8 bg-[#8C6D37] text-white font-mono text-[10px] px-3.5 py-1 uppercase tracking-[0.2em] font-bold rounded-none shadow-sm">
                РЕКОМЕНДУЕМЫЙ ФОРМАТ
              </div>
              <div className="flex flex-col gap-4 lg:max-w-[340px] pt-2">
                <div className="flex items-center gap-2.5">
                  <Brain size={16} className="text-[#8C6D37]" />
                  <span className="luxury-eyebrow text-[#8C6D37] font-bold tracking-wider uppercase">
                    УРОВЕНЬ 2. МЕТОДОЛОГИЯ
                  </span>
                </div>
                <h3 className="text-2xl font-serif font-semibold text-[#141416] leading-snug">
                  Внешнее методологическое кураторство <br />
                  <span className="text-[14px] font-sans font-normal text-[#8C6D37]">(Усиление компетенций)</span>
                </h3>
                <span className="font-mono text-[10px] text-[#8C6D37] bg-white/80 border border-[#8C6D37]/30 px-2.5 py-1 rounded-[2px] w-fit font-bold uppercase tracking-wider">
                  УСИЛИВАЕМ ШТАТНУЮ КОМАНДУ
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-[14px] font-sans font-normal text-[#334155] leading-relaxed flex-1 border-t lg:border-t-0 lg:border-l border-[#8C6D37]/30 pt-6 lg:pt-0 lg:pl-10">
                <div>
                  <strong className="text-[#8C6D37] block mb-1.5 font-bold">Когда применяется:</strong>
                  Штатный бухгалтер отлично ведет текучку, но не обязан знать тонкости крупных сделок M&A, правила применения 
                  Налоговой амнистии (176-ФЗ), раздельный учет НДС 2026 года или формулу целевого оборотного капитала (NWC Peg).
                </div>
                <div>
                  <strong className="text-[#8C6D37] block mb-1.5 font-bold">Что делаем мы:</strong>
                  Мы не увольняем бухгалтера. Мы ставим над процессами внешнего куратора — Временного финансового директора 
                  (Interim CFO) или Налогового адвоката. Он берет сложную методологию на себя, готовит финансовую модель 
                  и прикрывает штатную команду перед внешними аудиторами.
                </div>
              </div>
            </div>

            {/* УРОВЕНЬ 3 */}
            <div className="bg-white p-8 sm:p-10 rounded-[2px] border border-[#EAE5DE] shadow-xs flex flex-col lg:flex-row lg:items-start justify-between gap-8 hover:border-[#CBD5E1] transition-colors">
              <div className="flex flex-col gap-4 lg:max-w-[340px]">
                <div className="flex items-center gap-2.5">
                  <RefreshCw size={16} className="text-[#64748B]" />
                  <span className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase">
                    УРОВЕНЬ 3. РОТАЦИЯ
                  </span>
                </div>
                <h3 className="text-2xl font-serif font-semibold text-[#141416] leading-snug">
                  Точечная кадровая ротация <br />
                  <span className="text-[14px] font-sans font-normal text-[#64748B]">(Исключительная мера)</span>
                </h3>
                <span className="font-mono text-[10px] text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-1 rounded-[2px] w-fit font-bold uppercase tracking-wider">
                  КРАЙНЯЯ МЕРА ПРИ САБОТАЖЕ
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-[14px] font-sans font-normal text-[#334155] leading-relaxed flex-1 border-t lg:border-t-0 lg:border-l border-[#EAE5DE] pt-6 lg:pt-0 lg:pl-10">
                <div>
                  <strong className="text-[#141416] font-bold block mb-1.5">Когда применяется:</strong>
                  Сотрудник открыто саботирует подготовку к сделке, ворует базу пациентов или скрывает дефекты, 
                  создающие прямую угрозу уголовной ответственности (ст. 238 УК РФ по наркозу или ст. 238.1 по контрафактным имплантам).
                </div>
                <div>
                  <strong className="text-[#141416] font-bold block mb-1.5">Что делаем мы:</strong>
                  Мы не принимаем эмоциональных решений. Мы кладем на стол фаундеру сухие цифры выявленного риска 
                  и помогаем быстро и безопасно привлечь на ключевую позицию проверенного специалиста из нашего кадрового резерва.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================================
            3. ПЯТЬ МОДУЛЕЙ ТОЧЕЧНОГО УСИЛЕНИЯ (СПЕЦНАЗ ПОД ЗАДАЧУ)
            ========================================================================= */}
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <span className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase">
              ПЯТЬ МОДУЛЕЙ ТОЧЕЧНОГО УСИЛЕНИЯ // СПЕЦНАЗ ПОД ЗАДАЧУ:
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {specializedModules.map((mod, idx) => (
              <div 
                key={idx}
                className="bg-white p-8 rounded-[2px] border border-[#EAE5DE] shadow-xs flex flex-col justify-between gap-6 hover:border-[#CBD5E1] transition-colors group"
              >
                <div className="flex flex-col gap-4">
                  {/* Заголовок направления */}
                  <div className="flex items-center justify-between pb-3 border-b border-[#EAE5DE]">
                    <div className="flex items-center gap-2.5">
                      {mod.icon}
                      <span className="font-sans text-[14px] font-semibold text-[#141416]">
                        {mod.domain}
                      </span>
                    </div>
                    <span className="font-mono text-[11px] text-[#64748B] font-bold">
                      [{mod.num}]
                    </span>
                  </div>

                  {/* Эксперт */}
                  <div className="flex items-center gap-1.5 font-mono text-[11px] text-[#8C6D37] font-bold uppercase tracking-wider">
                    <ArrowRight size={12} />
                    <span>{mod.role}</span>
                  </div>

                  {/* Задача */}
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal pt-1">
                    <strong className="text-[#141416] font-bold">Задача:</strong> {mod.task}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* =========================================================================
            4. ИТОГОВАЯ ИНСТИТУЦИОНАЛЬНАЯ ГАРАНТИЯ
            ========================================================================= */}
        <div className="bg-[#8C6D37]/10 p-8 sm:p-10 rounded-[2px] border border-[#8C6D37]/40 flex flex-col gap-3 relative overflow-hidden shadow-xs">
          <div className="absolute inset-y-0 left-0 w-1.5 bg-[#8C6D37]" />
          <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2 font-bold pl-4 tracking-[0.1em] uppercase">
            <ShieldCheck size={16} className="text-[#8C6D37]" />
            <span>ПРИНЦИП ОПЕРАЦИОННОЙ НЕПРЕРЫВНОСТИ STRUCTURE & PARTNERS:</span>
          </div>
          <p className="text-[15px] sm:text-[16px] text-[#141416] leading-relaxed font-sans font-normal pl-4">
            Любые исправления в учете, договорах и процессах проводятся в фоновом режиме. <br className="hidden sm:inline" />
            Клиника продолжает вести непрерывный прием пациентов, врачи работают в привычном графике, 
            а выручка защищена от операционных сбоев.
          </p>
        </div>

        {/* =========================================================================
            5. НАВИГАТОР ПЕРЕХОДА К БЛОКУ 9
            ========================================================================= */}
        <div className="flex justify-center pt-4">
          <Link 
            href="#clinicos" 
            className="group inline-flex items-center gap-2 font-mono text-[11px] text-[#64748B] hover:text-[#141416] uppercase tracking-[0.15em] font-semibold transition-all py-3 px-6 hover:bg-black/5 border border-transparent hover:border-[#EAE5DE] rounded-[2px]"
          >
            <span>[ ПЕРЕЙТИ К ЭТАЖУ 9: АРХИТЕКТУРА АВТОНОМИИ S&P CLINIC OS™ ↓ ]</span>
          </Link>
        </div>

      </div>
    </section>
  );
}
